const parseBoolean = (value) => {
  if (value == null) return null;
  const normalised = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalised)) return true;
  if (["0", "false", "no", "off"].includes(normalised)) return false;
  return null;
};

const paymentsOverride = parseBoolean(
  process.env.REACT_APP_ENABLE_REMOTE_PAYMENTS ??
  process.env.REACT_APP_ALLOW_REMOTE_PAYMENTS ??
  process.env.REACT_APP_USE_REMOTE_PAYMENTS
);

const disableEmulatorsOverride = parseBoolean(
  process.env.REACT_APP_DISABLE_FIREBASE_EMULATORS ??
  process.env.REACT_APP_DISABLE_EMULATORS
);

const isRemotePaymentsEnabled = () => {
  // If explicitly set, respect the environment variable (true/false)
  if (paymentsOverride != null) {
    return paymentsOverride;
  }

  // Default: ENABLE remote payments even on localhost (use env to turn off)
  return true;
};

const shouldSkipApiRequest = (path) => {
  if (!path || typeof path !== 'string') return false;

  // Only suppress network calls for the payments namespace when disabled
  if ((path.startsWith('/payments') || path.startsWith('/api/payments')) && !isRemotePaymentsEnabled()) {
    return true;
  }

  return false;
};

const skippedWarnings = new Set();

const parseList = (value) => {
  if (!value || typeof value !== 'string') return [];
  return value
    .split(/[\s,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const dedupe = (values) => {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    if (!value) return;
    const normalised = value.replace(/\/+$/, '');
    if (!seen.has(normalised)) {
      seen.add(normalised);
      out.push(normalised);
    }
  });
  return out;
};

const defaultProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'zedipass';
const envRegions = parseList(process.env.REACT_APP_FUNCTIONS_REGION);
const defaultRegions = dedupe(envRegions.length ? envRegions : ['europe-west2']);

const buildCloudBase = (region, projectId) => `https://${region}-${projectId}.cloudfunctions.net/api`;
const buildEmulatorBase = (region, projectId) => `http://127.0.0.1:5001/${projectId}/${region}/api`;

const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname || '';
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
};

// Build direct function fallbacks for specific paths (bypasses /api aggregator)
const getDirectFunctionUrlsForPath = (_path) => {
  // Disable legacy direct-function fallbacks to avoid CORS/404 mismatches.
  return [];
};

/**
 * Returns an ordered list of API base URLs to try, deduplicated.
 */
export const getApiBaseCandidates = () => {
  const projectId = defaultProjectId;
  const customBases = dedupe(parseList(process.env.REACT_APP_API_BASES || process.env.REACT_APP_API_BASE_URL));
  const cloudBases = defaultRegions.map((region) => buildCloudBase(region, projectId));
  const emulatorBases =
    disableEmulatorsOverride === true
      ? []
      : defaultRegions.map((region) => buildEmulatorBase(region, projectId));
  const relativeBase = '/api';
  const relativeCustomBases = customBases.filter((base) => base.startsWith('/'));
  const remoteCustomBases = customBases.filter((base) => !base.startsWith('/'));

  if (isLocalhost()) {
    // In local development, prefer same-origin proxy routes first to avoid browser CORS failures.
    // If the proxy target is unavailable, fetchJsonWithFallback will continue through remote candidates.
    return dedupe([
      ...relativeCustomBases,
      relativeBase,
      ...remoteCustomBases,
      ...cloudBases,
      ...emulatorBases,
    ]);
  }

  return dedupe([
    ...customBases,
    ...cloudBases,
    relativeBase,
    ...emulatorBases,
  ]);
};

const joinBaseAndPath = (base, path) => {
  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
  if (!path) return cleanBase;
  let nextPath = path;

  if (cleanBase.endsWith('/api') && (nextPath === '/api' || nextPath.startsWith('/api/'))) {
    nextPath = nextPath.slice(4) || '/';
  }

  const prefix = nextPath.startsWith('/') ? '' : '/';
  return `${cleanBase}${prefix}${nextPath}`;
};

export const getApiUrls = (path) => {
  const directFns = getDirectFunctionUrlsForPath(path);
  const bases = getApiBaseCandidates();
  const baseUrls = !bases.length ? [path] : bases.map((base) => joinBaseAndPath(base, path));

  // Try direct Cloud Function first (avoids 404 on /api router misconfig & dodges CORS via CRA proxy when present)
  return dedupe([
    ...directFns,
    ...baseUrls,
  ]);
};

const defaultAcceptedHandler = async () => null;

/**
 * Fetch JSON from the API sequentially until one succeeds.
 * @param {string} path Path beginning with '/'.
 * @param {object} options
 * @param {RequestInit} [options.fetchOptions]
 * @param {boolean} [options.expectJson=true]
 * @param {number[]} [options.acceptStatuses=[]] HTTP statuses that should be treated as non-fatal.
 * @param {(status: number, response: Response) => Promise<any>} [options.handleAcceptedStatus]
 */
export const fetchJsonWithFallback = async (path, options = {}) => {
  const {
    fetchOptions = {},
    expectJson = true,
    acceptStatuses = [],
    handleAcceptedStatus = defaultAcceptedHandler,
  } = options;

  if (shouldSkipApiRequest(path)) {
    if (!skippedWarnings.has(path)) {
      skippedWarnings.add(path);
      console.info(
        `[apiClient] Skipping request to "${path}" because remote payments API access is disabled in local development. ` +
          'Set REACT_APP_ENABLE_REMOTE_PAYMENTS=true to override.'
      );
    }
    return null;
  }

  const urls = getApiUrls(path);
  let lastError;
  let acceptedValue;
  let sawAccepted = false;

  for (const url of urls) {
    try {
      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        if (acceptStatuses.includes(response.status)) {
          sawAccepted = true;
          acceptedValue = await handleAcceptedStatus(response.status, response);
          continue;
        }

        const text = await response.text().catch(() => '');
        const err = new Error(`HTTP ${response.status} from ${url}: ${text}`);
        err.status = response.status;
        err.url = url;
        lastError = err;
        continue;
      }

      if (!expectJson) {
        return response;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.toLowerCase().includes('application/json')) {
        const text = await response.text().catch(() => '');
        const err = new Error(`Expected JSON from ${url} but received ${contentType || 'unknown'} ${text ? `- ${text}` : ''}`);
        err.status = response.status;
        err.url = url;
        lastError = err;
        continue;
      }

      return await response.json();
    } catch (err) {
      lastError = err;
    }
  }

  if (sawAccepted) {
    return acceptedValue ?? null;
  }

  if (lastError) throw lastError;
  throw new Error(`All API requests failed for ${path}`);
};

export const buildJsonRequest = (method, body, extraHeaders = {}) => {
  const headers = { 'Content-Type': 'application/json', ...extraHeaders };
  const options = { method, headers };
  if (body !== undefined) {
    options.body = typeof body === 'string' ? body : JSON.stringify(body);
  }
  return options;
};
